import clsx from 'clsx';
import { Interval } from 'date-fns';
import { Breakpoints } from 'domains/forecast/utils';
import { ForecastProperty, ForecastSlice, MarketLocation } from 'generated/graphql';
import { useTranslation } from 'react-i18next';
import { DeviceTableFunctions } from './DeviceTableFunctions';
import { DeviceWithInstalledPower } from 'domains/forecast/control-energy-forecast/ControlEnergyForecast';
import { convertMeasure } from '@e2m-customer-portal/shared';

interface DeviceTableTitleProps {
  device: DeviceWithInstalledPower;
  marketLocation: MarketLocation;
  interval: Interval;
  slices: readonly ForecastSlice[];
  disableAssistant?: boolean;
  currentForecastProperty?: ForecastProperty;
}

export const DeviceTableTitle = ({
  device,
  marketLocation,
  interval,
  slices,
  disableAssistant,
  currentForecastProperty,
}: DeviceTableTitleProps) => {
  const [t] = useTranslation();
  const width = window.innerWidth;
  const installedPower = device.installedPower && convertMeasure(device.installedPower, 'kW');
  return (
    <div
      className="flex justify-between border-divider py-1 mt-6 border-0 border-y border-solid bg-white "
      data-testid={`${device.id}/${device.name}`}
    >
      <div className={clsx('flex flex-wrap items-center py-2 text-left xl:flex-nowrap xl:py-1')}>
        <div className="flex w-full justify-start">
          <div className="flex w-max items-center">
            <div className="ml-6 flex flex-col gap-1 whitespace-nowrap">
              <span className="text-xs">
                {device.externalName ? device.name : t('DEVICE_TABLE_HEADER_TECHNICAL_UNIT')}
              </span>
              <div className="sm:text-xl">
                <div className="relative flex items-center">{device.externalName || device.name}</div>
              </div>
            </div>
          </div>
          {installedPower && (
            <div className="flex w-max items-center ml-4 border-0 border-l border-divider border-solid">
              <div className="ml-2 flex flex-col gap-1 whitespace-nowrap">
                <span className="text-xs">{t('DEVICE_TABLE_HEADER_INSTALLED_POWER_LABEL')}</span>
                <div className="sm:text-xl">
                  <div className="relative flex items-center">
                    {t('DEVICE_TABLE_HEADER_INSTALLED_POWER', { value: installedPower.value })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {width > (Breakpoints.SM as number) && (
        <DeviceTableFunctions
          marketLocation={marketLocation}
          device={device}
          interval={interval}
          slices={slices}
          currentForecastProperty={currentForecastProperty}
          disableAssistant={disableAssistant}
        />
      )}
    </div>
  );
};
